import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { config } from "./config";
import { setToken, setUnauthorizedHandler } from "./api";

/** Claims we read client-side purely to label the UI. The server re-verifies
 *  every token on every request — nothing here is trusted for authorization. */
export interface TokenProfile {
  email: string;
  name?: string;
  picture?: string;
}

interface AuthValue {
  profile: TokenProfile | null;
  /** GIS script loaded and initialize() called. */
  ready: boolean;
  signOut: () => void;
}

const AuthContext = createContext<AuthValue>({
  profile: null,
  ready: false,
  signOut: () => {},
});

export const useAuth = () => useContext(AuthContext);

function decodeJwtPayload(jwt: string): TokenProfile | null {
  try {
    const [, payload] = jwt.split(".");
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as TokenProfile;
    return claims.email ? claims : null;
  } catch {
    return null;
  }
}

/** Resolves once the GIS script has attached window.google. */
function waitForGis(timeoutMs = 10_000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const started = Date.now();
    const tick = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(tick);
        resolve();
      } else if (Date.now() - started > timeoutMs) {
        clearInterval(tick);
        reject(new Error("Google Sign-In failed to load."));
      }
    }, 50);
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<TokenProfile | null>(null);
  const [ready, setReady] = useState(false);
  const initialized = useRef(false);

  const handleCredential = useCallback((res: { credential: string }) => {
    setToken(res.credential);
    setProfile(decodeJwtPayload(res.credential));
  }, []);

  const signOut = useCallback(() => {
    setToken(null);
    setProfile(null);
    // Without this, auto_select immediately signs the same account back in and
    // "sign out" appears to do nothing.
    window.google?.accounts.id.disableAutoSelect();
  }, []);

  useEffect(() => {
    if (!config.googleClientId) return;
    let alive = true;

    // The `initialized` guard lives INSIDE the promise, not at the top of the
    // effect. Its job is only to avoid calling GIS initialize() twice. Guarding
    // the effect body with it instead would deadlock under StrictMode, which
    // mounts → cleans up → mounts again: the second mount would short-circuit on
    // the ref while the first mount's promise bailed on its cancellation flag,
    // so `ready` would never flip and the app would sit on "Loading…" forever —
    // and only when GIS loaded *successfully*, since a load failure still lands
    // in .catch below.
    waitForGis()
      .then(() => {
        if (!alive) return;
        if (!initialized.current) {
          initialized.current = true;
          window.google!.accounts.id.initialize({
            client_id: config.googleClientId,
            callback: handleCredential,
            // ID tokens last ~1h, so re-authentication is routine. auto_select
            // makes it silent for an account that has already consented.
            auto_select: true,
            cancel_on_tap_outside: false,
          });
          // Exactly one prompt() on startup, inside the same guard. FedCM allows
          // only one outstanding navigator.credentials.get(), so a second
          // concurrent call rejects with NotAllowedError and kills the silent
          // re-auth that lets us keep the token in memory only.
          window.google!.accounts.id.prompt();
        }
        setReady(true);
      })
      .catch(() => {
        // Render the sign-in view anyway; it surfaces the failure rather than
        // leaving a spinner with no explanation.
        if (alive) setReady(true);
      });

    return () => {
      alive = false;
    };
  }, [handleCredential]);

  // A 401 means the token aged out mid-session. Drop it and let GIS mint a new
  // one; the sign-in view re-renders the button if that needs a click.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setToken(null);
      setProfile(null);
      window.google?.accounts.id.prompt();
    });
  }, []);

  return (
    <AuthContext.Provider value={{ profile, ready, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

/** The Google-rendered sign-in button. GIS owns this DOM node. */
export function GoogleSignInButton() {
  const ref = useRef<HTMLDivElement>(null);
  const { ready } = useAuth();

  useEffect(() => {
    if (!ready || !ref.current || !window.google) return;
    // renderButton only. prompt() is owned by AuthProvider — calling it here too
    // raced the startup One Tap and rejected with FedCM's NotAllowedError.
    window.google.accounts.id.renderButton(ref.current, {
      theme: "outline",
      size: "large",
      text: "signin_with",
      shape: "rectangular",
      width: 280,
    });
  }, [ready]);

  return <div ref={ref} />;
}
